import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

import { RootState } from '../../../store';
import {
  setRowData,
  setSelectedCounselling,
} from '../../../store/slices/counsellingSlice';
import {
  useGetStaffQuery,
  useLazyGetOrderTemplateQuery,
  useLazyGetPdfOrderCopyQuery,
  useLazyGetUploadedFilesQuery,
} from '../counselling.services';
import './order-copy-pdf.scss';
import { SUBJECT_DESIGNATIONS } from '../../../shared/services/commonService';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `OrderCopyPdfComponent`
 * (src/app/views/pages/counselling/order-copy-pdf).
 *
 * Printable transfer/promotion/relinquish order copy (Tamil govt order). Reached
 * from DEE's "Download Report" button, which stores the selected row +
 * counselling in Redux (rowData / selectedCounselling) and `localStorage.type`,
 * then navigates here.
 *
 * The template Header/Reference/Content/etc. are server-provided HTML strings,
 * rendered via dangerouslySetInnerHTML (the direct equivalent of Angular's
 * [innerHTML]). This content is controlled by the counselling backend.
 */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

/**
 * The order-copy record returned by getPdfOrderCopy, plus the three fields we
 * derive on the client: `order_copy_grid` (parsed from JSON) and the resolved
 * `desgnationName` / `newDesgnationName` labels. The API record is open (any),
 * so the index signature keeps the many template fields accessible.
 */
interface OrderTemplateData {
  order_copy_grid: any[];
  desgnationName: any;
  newDesgnationName: any;
  [key: string]: any;
}

/** Format a date value as dd/MM/yyyy (Angular date: 'dd/MM/yyyy'). */
const formatDate = (value: any): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/** Render a server-provided HTML string (Angular [innerHTML]). */
const Html = ({ html, style }: { html: any; style?: React.CSSProperties }) => (
  <div style={style} dangerouslySetInnerHTML={{ __html: html ?? '' }} />
);

const InlineHtml = ({ html }: { html: any }) => (
  <span
    className="inline-style"
    dangerouslySetInnerHTML={{ __html: html ?? '' }}
  />
);

const OrderCopyPdf = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector((state: RootState) => state.session);

  // Hand-off state (Angular authenticationService.getRowDataVal / getCounsellingData)
  const selectRowData = useSelector(
    (state: RootState) => state.counselling.rowData
  );
  const selectCnData = useSelector(
    (state: RootState) => state.counselling.selectedCounselling
  );

  const districtId = Number(session.userId ?? 0);
  const userType = str(session.usertypeid);
  const userType1 = str(session.emisUsertype1);
  const isScertRole =
    (userType === '5' && userType1 === '6') ||
    (userType === '5' && userType1 === '7') ||
    userType === '22';

  const currentDate = useMemo(() => new Date(), []);
  // Order-copy type comes from the localStorage hand-off (Angular parity).
  const type = useMemo(() => {
    const t = localStorage.getItem('type');
    return t === 'null' || !t ? '1' : t;
  }, []);

  const { data: staffRaw } = useGetStaffQuery();
  const [triggerOrderTemplate] = useLazyGetOrderTemplateQuery();
  const [triggerPdfOrderCopy] = useLazyGetPdfOrderCopyQuery();
  const [triggerUploadedFiles] = useLazyGetUploadedFilesQuery();

  const [orderTemplate, setOrderTemplate] = useState<OrderTemplateData | null>(
    null
  );
  const [orderTemplateContent, setOrderTemplateContent] = useState<any>(null);
  const [signature, setSignature] = useState('');
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);

  // Staff list — SCERT roles use the Tamil staff type list.
  const staffTypeList: any[] = useMemo(() => {
    if (!staffRaw) return [];
    return isScertRole
      ? (staffRaw.SCERTTamilStaffType ?? [])
      : (staffRaw.AllStaffType ?? []);
  }, [staffRaw, isScertRole]);

  // ── RcFlag (identical to DEE onRowPDF / Angular getOrderCopyValue) ──────────
  const rcFlag = useMemo(() => {
    if (
      districtId === 16 &&
      userType === '10' &&
      userType1 === '2' &&
      str(selectCnData?.Location) === '1'
    ) {
      return '2';
    }
    if (
      userType === '9' &&
      userType1 === '29' &&
      str(selectCnData?.CounsellingType) === '9'
    ) {
      return '3';
    }
    if (
      str(selectCnData?.schType) === '2' &&
      str(selectCnData?.Location) === '4' &&
      userType === '10' &&
      userType1 === '3'
    ) {
      return '2';
    }
    return selectCnData?.Location;
  }, [districtId, userType, userType1, selectCnData]);

  // ── Signature file ──────────────────────────────────────────────────────────
  const getUploadedFiles = useCallback(
    async (photo: string) => {
      try {
        const result: any = await triggerUploadedFiles({
          bucketName: 'renewalapplicationemis',
          fileName: photo,
          expiry: 1800,
        }).unwrap();
        if (result?.url) setSignature(result.url);
      } catch {
        /* ignore */
      }
    },
    [triggerUploadedFiles]
  );

  // ── Order data (Angular getOrderCopyValue -> getPdfOrderCopy) ───────────────
  const getOrderCopyValue = useCallback(async () => {
    const data = {
      counsellingId: selectCnData?.IndexId,
      rcFlag,
      type: type ? type : '1',
      teacherId:
        str(selectCnData?.CounsellingType) === '4'
          ? `'${selectRowData?.application_id}'`
          : selectRowData?.teacher_id,
      teacherStatus: selectRowData?.teacher_status,
      subject: SUBJECT_DESIGNATIONS.includes(str(selectCnData?.Designation))
        ? selectRowData?.subject
        : 'null',
      counsellingType: selectCnData?.CounsellingType,
    };
    try {
      const res: any = await triggerPdfOrderCopy(data).unwrap();
      if (res?.dataStatus) {
        const raw = res.result.length > 0 ? res.result[0] : res.result;

        const desi = staffTypeList.filter(
          (sf) => str(sf.value) === str(raw?.designation)
        );
        const desiNew = staffTypeList.filter(
          (sf) => str(sf.value) === str(raw?.new_designation)
        );

        // Build a typed record: spread the API fields, then add the three
        // derived keys explicitly so they always exist on the template.
        const tpl: OrderTemplateData = {
          ...raw,
          order_copy_grid: raw?.order_copy_grid
            ? JSON.parse(raw.order_copy_grid)
            : [],
          desgnationName: desi.length > 0 ? desi[0].label : desi,
          newDesgnationName: desiNew.length > 0 ? desiNew[0].label : desiNew,
        };

        setOrderTemplate(tpl);
        if (res.result[0]?.Signature) {
          getUploadedFiles(res.result[0].Signature);
        }
      }
    } catch {
      /* ignore */
    }
    setIsRefreshLoading(false);
  }, [
    selectCnData,
    selectRowData,
    rcFlag,
    type,
    triggerPdfOrderCopy,
    staffTypeList,
    getUploadedFiles,
  ]);

  // ── Template content (Angular getOrderCopyContent -> GetOrderTemplate) ──────
  const getOrderCopyContent = useCallback(async () => {
    try {
      const res: any = await triggerOrderTemplate({
        counsellingId: selectCnData?.IndexId,
        type: type ? type : '1',
      }).unwrap();
      if (res?.dataStatus) {
        setOrderTemplateContent(res.result[0]);
        getOrderCopyValue();
      }
    } catch {
      /* ignore */
    }
  }, [triggerOrderTemplate, selectCnData, type, getOrderCopyValue]);

  const loadAll = useCallback(() => {
    getOrderCopyContent();
    const t = setTimeout(() => setIsRefreshLoading(false), 1000);
    return () => clearTimeout(t);
  }, [getOrderCopyContent]);

  useEffect(() => {
    if (!staffRaw) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffRaw]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const onRowPDF = useCallback(() => window.print(), []);

  // Angular refreshBtn() re-runs ngOnInit(): reset transient view state and
  // re-run the full load (template -> order data -> signature).
  const refreshBtn = useCallback(() => {
    setIsRefreshLoading(true);
    setSignature('');
    setOrderTemplate(null);
    setOrderTemplateContent(null);
    loadAll();
  }, [loadAll]);

  const handleGoBack = useCallback(() => {
    if (selectRowData && selectRowData.IsMutual === 'Mutual') {
      dispatch(setRowData({ ...selectRowData, IsMutual: 'Mutual' }));
      dispatch(setSelectedCounselling(selectCnData));
      navigate('/counselling/teacher-transfer');
    } else if (selectRowData && selectRowData.IsMutual === '') {
      dispatch(setRowData({ ...selectRowData, IsMutual: '' }));
      dispatch(setSelectedCounselling(selectCnData));
      navigate(-1);
    } else {
      navigate(-1);
    }
  }, [selectRowData, selectCnData, dispatch, navigate]);
  // ── Derived template flags for readability ──────────────────────────────────
  const odrTmplte = orderTemplate;
  const odrTmplteCntnt = orderTemplateContent;
  const cType = str(selectCnData?.CounsellingType);
  const directorate = str(selectCnData?.Directorate);
  const desig = str(odrTmplte?.designation);
  const newDesig = str(odrTmplte?.new_designation);
  const tplCType = str(odrTmplte?.counsellingType);

  const subjectDesigs = SUBJECT_DESIGNATIONS;
  const isSubjectDesig = subjectDesigs.includes(desig);
  const isNewSubjectDesig = subjectDesigs.includes(newDesig);

  return (
    <div className="total-style">
      {/* Action buttons (hidden on print) */}
      <div id="printPageButton" className="row tc">
        <div className="col text-right">
          <Button
            type="button"
            className="p-button-success"
            icon={isRefreshLoading ? 'pi pi-spin pi-spinner' : ''}
            label={isRefreshLoading ? '' : 'Refresh'}
            onClick={refreshBtn}
          />
          &nbsp;
          <Button
            type="button"
            label="Print"
            className="p-button-info"
            onClick={onRowPDF}
          />
          &nbsp;
          <Button
            type="button"
            label="Go Back"
            className="p-button-danger"
            onClick={handleGoBack}
          />
        </div>
      </div>

      <div className="tc">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="row">
          <div className="col-12 text-center printUI">
            <div className="heading-ptag">
              {((str(odrTmplte?.Location) !== '3' &&
                str(odrTmplte?.Location) !== '5' &&
                directorate !== '4') ||
                (cType === '1' && str(selectCnData?.schType) === '2')) && (
                <span className="inline-style">
                  {desig !== '102' && <b>{odrTmplte?.tamil_name} &nbsp;</b>}
                </span>
              )}
              {cType === '4' && str(odrTmplte?.Location) !== '3' && (
                <span>
                  <b>{odrTmplte?.NewDistrictName}&nbsp; </b>
                </span>
              )}
              <InlineHtml html={odrTmplteCntnt?.Header} />
            </div>
            <div>
              {desig !== '103' && (
                <span>
                  {directorate === '6' && <span>ஆணை பிறப்பிப்பவர்: </span>}
                  {directorate !== '6' && <span>முன்னிலை: </span>}
                </span>
              )}
              {odrTmplte?.officerNme} <br />
              {desig !== '103' && (
                <span>
                  {odrTmplte?.RCNum}, நாள்.{' '}
                  {str(odrTmplte?.counselling_id) === '87' ? (
                    <span>{formatDate(currentDate)}</span>
                  ) : (
                    <span>{formatDate(odrTmplte?.FromDate)}</span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <br />

        {/* ── Reference (designation 103) ────────────────────────────── */}
        {desig === '103' && (
          <>
            <div className="row pb-4 printUI">
              <div className="col">
                <b>{odrTmplte?.RCNum}</b>
              </div>
              <div className="col text-right">
                <b>நாள். {formatDate(odrTmplte?.FromDate)}</b>
              </div>
            </div>
            <div className="row">
              <div className="col-12 printUI p1-4 pr-4">
                <div className="row">
                  <div className="col-2 text-right">
                    <b>பொருள்</b>
                  </div>
                  <div className="col pl-2">
                    <Html html={odrTmplteCntnt?.Reference} />
                  </div>
                  <div className="col-1" />
                </div>
                <div className="row">
                  <div className="col-2 text-right">
                    <b>பார்வை</b>
                  </div>
                  <div className="col">
                    <Html html={odrTmplteCntnt?.Reference2} />
                  </div>
                  <div className="col-1" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Reference table (non-103) ──────────────────────────────── */}
        {desig !== '103' && (
          <div className="row">
            <div className="col-12 printUI">
              <table className="width-table-porul width-table">
                <tbody>
                  <tr>
                    <td>
                      <b>பொருள்</b>
                    </td>
                    <td className="heading-ptag">
                      <Html html={odrTmplteCntnt?.Reference} />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>பார்வை</b>
                    </td>
                    <td className="heading-ptag" style={{ textAlign: 'left' }}>
                      <Html html={odrTmplteCntnt?.Reference2} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Before-candidate content ───────────────────────────────── */}
        <div className="row">
          <div className="col text-center printUI">
            *****
            <Html
              html={odrTmplteCntnt?.ContentBefore}
              style={{ textAlign: 'justify', wordWrap: 'break-word' }}
            />
          </div>
        </div>

        {/* ── Main candidate details table (not type 8/4, not SCERT) ─── */}
        {cType !== '8' && directorate !== '5' && cType !== '4' && (
          <div className="row">
            <div className="col-12 printUI">
              <table className="width-table">
                <tbody>
                  {tplCType === '4' && (
                    <tr>
                      <td>குறிப்பு எண்</td>
                      <td>-</td>
                    </tr>
                  )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>EMIS எண்</td>
                      <td>
                        {desig === '103' ? (
                          <b>{odrTmplte?.teacher_id}</b>
                        ) : (
                          <span>{odrTmplte?.teacher_id}</span>
                        )}
                      </td>
                    </tr>
                  )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>பெயர்</td>
                      <td>
                        {desig === '103' ? (
                          <b>{odrTmplte?.name}</b>
                        ) : (
                          <span>{odrTmplte?.name}</span>
                        )}
                      </td>
                    </tr>
                  )}
                  {(tplCType !== '2' ||
                    (`${tplCType}` !== '1' &&
                      userType === '10' &&
                      userType1 === '2')) &&
                    str(selectCnData?.schType) !== '2' &&
                    tplCType !== '1' && (
                      <tr>
                        <td>முன்னுரிமை எண்</td>
                        <td>{odrTmplte?.seniority_number}</td>
                      </tr>
                    )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>
                        {str(selectCnData?.schType) !== '2' &&
                          desig !== '102' && (
                            <span>தற்போதைய பதவி & பாடம் </span>
                          )}
                        {str(selectCnData?.schType) === '2' &&
                          desig !== '102' && <span>பதவி & பாடம் </span>}
                        {desig === '102' && <span>பதவி</span>}
                      </td>
                      <td>
                        {desig === '103' ? (
                          <b>
                            {odrTmplte?.desgnationName} - {odrTmplte?.oldSub}
                          </b>
                        ) : (
                          <span>
                            {odrTmplte?.desgnationName}
                            {isSubjectDesig && (
                              <span>
                                {' '}
                                -{' '}
                                {str(odrTmplte?.subject) === '26' ||
                                str(odrTmplte?.subject) === '27'
                                  ? odrTmplte?.postName
                                  : odrTmplte?.oldSub}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>
                        {cType === '3'
                          ? 'தற்போது பணிபுரியும் இடம்'
                          : (cType === '2' && desig === '103') ||
                              desig === '102'
                            ? 'தற்போது பணிபுரியும் அலுவலகம், மாவட்டம்'
                            : 'தற்போது பணிபுரியும் பள்ளியின் பெயர், UDISE, ஒன்றியம், மாவட்டம்'}
                      </td>
                      <td>
                        {desig === '103' ? (
                          <b>
                            {odrTmplte?.OldSchool}, {odrTmplte?.oldUdise},{' '}
                            {odrTmplte?.oldBlockName},{' '}
                            {odrTmplte?.oldDistrictName}
                          </b>
                        ) : desig !== '102' ? (
                          <span>
                            {odrTmplte?.OldSchool}, {odrTmplte?.oldUdise},{' '}
                            {odrTmplte?.oldBlockName},{' '}
                            {odrTmplte?.oldDistrictName}
                          </span>
                        ) : (
                          <span>
                            {odrTmplte?.oldBlockName},{' '}
                            {odrTmplte?.oldDistrictName}
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                  {type === '1' &&
                    str(selectCnData?.schType) === '1' &&
                    desig !== '102' && (
                      <tr>
                        <td>
                          {cType === '3'
                            ? 'பதவி உயர்வு வழங்கப்படும் பதவி'
                            : 'மாறுதல் செய்யப்படும் பதவி & பாடம்'}
                        </td>
                        <td>
                          {desig === '103' ? (
                            <b>
                              {odrTmplte?.newDesgnationName}
                              {str(odrTmplte?.new_designation) === '103' && (
                                <span> - {odrTmplte?.NewSub}</span>
                              )}
                            </b>
                          ) : (
                            <span>
                              {odrTmplte?.newDesgnationName}
                              {isNewSubjectDesig && (
                                <span> - {odrTmplte?.NewSub} </span>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                  {type === '1' && (
                    <tr>
                      <td>
                        {cType === '3'
                          ? 'பதவி உயர்வு வழங்கப்படும் இடம்'
                          : (cType === '2' && desig === '103') ||
                              desig === '102'
                            ? 'மாறுதல் செய்யப்படும் அலுவலகம், மாவட்டம்'
                            : 'மாறுதல் செய்யப்படும் பள்ளியின் பெயர், UDISE, ஒன்றியம், மாவட்டம்'}
                      </td>
                      <td>
                        {desig === '103' ? (
                          <b>
                            {odrTmplte?.NewSchl}, {odrTmplte?.NewUdise},{' '}
                            {odrTmplte?.NewBlockName},{' '}
                            {odrTmplte?.NewDistrictName}
                          </b>
                        ) : desig !== '102' ? (
                          <span>
                            {odrTmplte?.NewSchl}, {odrTmplte?.NewUdise},{' '}
                            {odrTmplte?.NewBlockName},{' '}
                            {odrTmplte?.NewDistrictName}
                          </span>
                        ) : (
                          <span>
                            {odrTmplte?.NewBlockName},{' '}
                            {odrTmplte?.NewDistrictName}
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                  {str(selectCnData?.schType) === '2' && (
                    <tr>
                      <td>Employee IFHRMS ID</td>
                      <td>{odrTmplte?.ifhrms_id}</td>
                    </tr>
                  )}
                  {(tplCType === '1' ||
                    tplCType === '2' ||
                    (tplCType !== '1' &&
                      userType === '10' &&
                      userType1 === '2')) &&
                    desig !== '102' &&
                    directorate !== '4' && (
                      <tr>
                        <td>முன்னுரிமை விவரம்</td>
                        <td>
                          {desig === '103' ? (
                            <b>{odrTmplte?.Priority_Description}</b>
                          ) : (
                            <span>{odrTmplte?.Priority_Description}</span>
                          )}
                        </td>
                      </tr>
                    )}
                  {directorate === '6' && (
                    <tr>
                      <td>சிறப்பு முன்னுரிமை விவரம்</td>
                      <td>
                        {str(odrTmplte?.special_priority) === '1'
                          ? 'ஆம்'
                          : 'இல்லை'}
                      </td>
                    </tr>
                  )}
                  {tplCType === '1' && desig !== '102' && (
                    <tr>
                      <td>இடுகை வகை</td>
                      <td>
                        {str(odrTmplte?.vac_need_flag) === '1'
                          ? 'Need'
                          : 'Vacancy'}
                      </td>
                    </tr>
                  )}
                  {type === '2' && (
                    <tr>
                      <td>உரிமைவிடல் தன்மை (தற்காலிகம் / நிரந்தரம்)</td>
                      <td>
                        {str(odrTmplte?.relin_value) === '1'
                          ? 'தற்காலிகம்'
                          : 'நிரந்தரம்'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SCERT (Directorate 5) table ────────────────────────────── */}
        {directorate === '5' && (
          <div>
            <div className="col-12">
              <table className="width-table">
                <tbody>
                  {tplCType === '4' && (
                    <tr>
                      <td>குறிப்பு எண்</td>
                      <td>-</td>
                    </tr>
                  )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>கல்வியாளரின் EMIS எண்</td>
                      <td>{odrTmplte?.teacher_id}</td>
                    </tr>
                  )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>பெயர்</td>
                      <td>{odrTmplte?.name}</td>
                    </tr>
                  )}
                  <tr>
                    <td>முன்னுரிமை எண்</td>
                    <td>{odrTmplte?.seniority_number}</td>
                  </tr>
                  {tplCType !== '4' && (
                    <tr>
                      <td>தற்போதைய பதவி & பாடம்</td>
                      <td>
                        {odrTmplte?.desgnationName}
                        {isSubjectDesig && <span> - {odrTmplte?.oldSub} </span>}
                      </td>
                    </tr>
                  )}
                  {tplCType !== '4' && (
                    <tr>
                      <td>தற்போது பணிபுரியும் நிறுவனத்தின் பெயர், மாவட்டம்</td>
                      <td>{odrTmplte?.tamil_name}</td>
                    </tr>
                  )}
                  {type === '1' && (
                    <tr>
                      <td>
                        {cType === '3'
                          ? 'பதவி உயர்வு வழங்கப்படும் பதவி'
                          : 'மாறுதல் செய்யப்படும் பதவி & பாடம்'}
                      </td>
                      <td>
                        {odrTmplte?.newDesgnationName}
                        {isNewSubjectDesig && (
                          <span> - {odrTmplte?.NewSub} </span>
                        )}
                      </td>
                    </tr>
                  )}
                  {type === '1' && (
                    <tr>
                      <td>
                        {cType === '3'
                          ? 'பதவி உயர்வு வழங்கப்படும் இடம்'
                          : 'மாறுதல் செய்யப்படும் நிறுவனத்தின் பெயர், மாவட்டம்'}
                      </td>
                      <td>{odrTmplte?.new_tamil_name}</td>
                    </tr>
                  )}
                  {type === '2' && (
                    <tr>
                      <td>உரிமைவிடல் தன்மை (தற்காலிகம் / நிரந்தரம்)</td>
                      <td>
                        {str(odrTmplte?.relin_value) === '1'
                          ? 'தற்காலிகம்'
                          : 'நிரந்தரம்'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Mutual (CounsellingType 8) two-teacher table ───────────── */}
        {cType === '8' && (
          <div className="row">
            <div className="col-12">
              <table
                className="border"
                style={{ width: '100%', padding: '10px' }}
              >
                <tbody>
                  <tr className="border">
                    <th className="border-end p-2">Title</th>
                    <th className="border-end p-2">Teacher 1</th>
                    <th className="border-end p-2">Teacher 2</th>
                  </tr>
                  <tr className="border">
                    <th className="border-end p-2">Teacher ID</th>
                    <td className="border-end p-2">{odrTmplte?.teacher_id}</td>
                    <td className="border-end p-2">
                      {selectRowData?.TransTchId}
                    </td>
                  </tr>
                  <tr className="border">
                    <th className="border-end p-2">Name</th>
                    <td className="border-end p-2">{odrTmplte?.name}</td>
                    <td className="border-end p-2">
                      {selectRowData?.TransTchNme}
                    </td>
                  </tr>
                  <tr className="border">
                    <th className="border-end p-2">Designation</th>
                    <td className="border-end p-2">
                      {odrTmplte?.desgnationName}
                    </td>
                    <td className="border-end p-2">
                      {odrTmplte?.newDesgnationName}
                    </td>
                  </tr>
                  <tr className="border">
                    <th className="border-end p-2">Subject</th>
                    <td className="border-end p-2">{odrTmplte?.oldSub}</td>
                    <td className="border-end p-2">{odrTmplte?.oldSub}</td>
                  </tr>
                  <tr className="border">
                    <th className="border-end p-2">UDISE</th>
                    <td className="border-end p-2">{odrTmplte?.oldUdise}</td>
                    <td className="border-end p-2">
                      {selectRowData?.TransUdise}
                    </td>
                  </tr>
                  <tr className="border">
                    <th className="border-end p-2">School Name</th>
                    <td className="border-end p-2">
                      {odrTmplte?.OldSchool} - {odrTmplte?.oldDistrictName}
                    </td>
                    <td className="border-end p-2">
                      {odrTmplte?.NewSchl} - {odrTmplte?.NewDistrictName}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Application-based (CounsellingType 4) dynamic grid ──────── */}
        {cType === '4' && (
          <div className="mt-4">
            <table className="width-table">
              <tbody>
                {(odrTmplte?.order_copy_grid ?? []).map(
                  (item: any, ind: number) => (
                    <tr key={ind}>
                      <td style={{ width: '50%' }}>{item.key}</td>
                      <td style={{ width: '50%' }}>
                        {(item.value ?? []).map(
                          (valItem: string, subInd: number) => (
                            <span key={subInd}>
                              {(valItem === 'pdf_file' ||
                                valItem === 'signature') &&
                                (odrTmplte?.pdf_file ||
                                  odrTmplte?.signature) && (
                                  <img
                                    src={odrTmplte[valItem]}
                                    width="100px"
                                    height="100px"
                                    alt=""
                                  />
                                )}
                              {valItem !== 'pdf_file' &&
                                valItem !== 'signature' && (
                                  <span>
                                    {odrTmplte && odrTmplte[valItem]}
                                    {subInd !== item.value.length - 1 && (
                                      <>, </>
                                    )}
                                  </span>
                                )}
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── After-candidate content ────────────────────────────────── */}
        <div className="row">
          <div className="col-12 pt-3 printUI">
            <Html
              html={odrTmplteCntnt?.ContentAfter}
              style={{ textAlign: 'justify', wordWrap: 'break-word' }}
            />
          </div>
        </div>

        {/* ── Signature image ────────────────────────────────────────── */}
        <div className="row">
          <div className="col-12 text-right">
            {signature && (
              <img
                src={signature}
                width="200px"
                height="70px"
                alt="signature"
              />
            )}
          </div>
        </div>

        {/* ── Sign authority ─────────────────────────────────────────── */}
        <div className="row">
          <div className="col-12 text-right printUI">
            <InlineHtml html={odrTmplteCntnt?.SignAuth} />
            {str(odrTmplte?.Location) !== '3' &&
              str(odrTmplte?.Location) !== '5' &&
              cType !== '9' && (
                <span className="inline-style">
                  {desig !== '102' && (
                    <span> &nbsp;-&nbsp; {odrTmplte?.tamil_name}</span>
                  )}
                </span>
              )}
          </div>
        </div>

        {/* ── Copy to (பெறுநர்) ──────────────────────────────────────── */}
        <div className="row">
          {cType !== '8' && (
            <div className="col-12 printUI">
              <b>பெறுநர் </b>
              <br />
              <span>
                {odrTmplte?.name} (
                {cType === '4'
                  ? odrTmplte?.application_id
                  : odrTmplte?.teacher_id}
                ) -{' '}
              </span>
              <span>
                {odrTmplte?.desgnationName}
                {isSubjectDesig && <span> - {odrTmplte?.oldSub}</span>}
              </span>
              {directorate !== '5' && desig !== '102' && cType !== '4' && (
                <span>
                  {' '}
                  - {odrTmplte?.oldUdise} - {odrTmplte?.OldSchool} -{' '}
                  {odrTmplte?.oldBlockName} - {odrTmplte?.oldEduDistrictName} -{' '}
                  {odrTmplte?.oldDistrictName}
                </span>
              )}
              {desig === '102' && (
                <span>
                  {' '}
                  - {odrTmplte?.oldBlockName} - {odrTmplte?.oldEduDistrictName}{' '}
                  - {odrTmplte?.oldDistrictName}
                </span>
              )}
              {directorate === '5' && <span> - {odrTmplte?.tamil_name}</span>}
              <br />
              <Html html={odrTmplteCntnt?.CopyTo} />
            </div>
          )}

          {cType === '8' && (
            <>
              <div className="col-12 printUI">
                <b>பெறுநர் </b>
                <br />
                <span>
                  {odrTmplte?.name} ({odrTmplte?.teacher_id}),{' '}
                </span>
                <span>
                  {odrTmplte?.desgnationName}
                  {isSubjectDesig && <span> - {odrTmplte?.oldSub}</span>}
                </span>
                <span>
                  {' '}
                  - {odrTmplte?.oldUdise} - {odrTmplte?.OldSchool} -{' '}
                  {odrTmplte?.oldBlockName} - {odrTmplte?.oldEduDistrictName} -{' '}
                  {odrTmplte?.oldDistrictName}
                </span>
              </div>
              <div className="col-12 pt-3 printUI">
                <span>
                  {selectRowData?.TransTchNme} ({selectRowData?.TransTchId}
                  ),{' '}
                </span>
                <span>
                  {odrTmplte?.newDesgnationName}
                  {isNewSubjectDesig && <span> - {odrTmplte?.oldSub}</span>}
                </span>
                <span>
                  {selectRowData?.TransUdise} - {odrTmplte?.NewSchl} -{' '}
                  {odrTmplte?.NewBlockName} - {odrTmplte?.NewEduDistrictName} -{' '}
                  {odrTmplte?.NewDistrictName}
                </span>
                <br />
                <Html html={odrTmplteCntnt?.CopyTo} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderCopyPdf;

/* eslint-enable @typescript-eslint/no-explicit-any */
